import {useTranslation} from "ni18n";
import React, {useState} from "react";
import {LightLeverJobPostingResponse} from "src/_services/types";
import {careersUrl} from "src/components/_common/_constants";
import {V5Img} from "src/components/_common/Carbon";
import {useTypedSelector} from "src/store";
import useMediaQuery from "src/useMediaQuery";

import {Btn} from "../V5Comp";

export type Props = {
  name: string;
  jobs: LightLeverJobPostingResponse[];
  backgroundColor: string;
  color: string;
  backgroundImage?: string;
  zIndex?: number;
};

const JobGroupCard: React.FC<Props> = ({
  name,
  zIndex,
  jobs,
  backgroundColor,
  backgroundImage,
  color,
}) => {
  const i18n = useTranslation();
  const {isMobile} = useTypedSelector(state => state.config);
  const [showTeams, setShowTeams] = useState(false);
  const isXs = useMediaQuery("xs");

  const leverDeptUrl = `${careersUrl}?department=${name.replace("&", "%26")}`;

  const teams = jobs.distinctBy(job => job.categories.team).map(job => job.categories.team);

  const extraTeams = Math.max(teams.length - 6, 0);

  const toggleShowTeams = (boolean?: boolean) => () => {
    setShowTeams(typeof boolean === "boolean" ? boolean : !showTeams);
  };

  const expandedHeight = extraTeams * 6.3 + 100 - (name.length < 19 ? 10 : 0);

  const wrapperProps = {
    href: isMobile ? null : leverDeptUrl,
    className:
      "pos-r ph4 pt3 pb2 mb2 mb5-xs mr2 mr0-xs minh72 minh minh36-xs mh0-xs br2 cp t-300 oh df aife w72 w100p-f-xs",
    style: {
      backgroundColor,
      height: showTeams ? `${expandedHeight}%` : "0%",
      maxHeight: 278 + (showTeams && extraTeams ? 500 : 0),
      transform: showTeams && !isXs ? "scale(1.04)" : null,
    },
    onClick: toggleShowTeams(),
    onMouseEnter: toggleShowTeams(true),
    onFocus: toggleShowTeams(true),
    onMouseLeave: toggleShowTeams(false),
    onBlur: toggleShowTeams(false),
  };

  const children = [
    <V5Img
      key={`pattern-${leverDeptUrl}`}
      className={`pos-a top0 right0 zIndex0 t-300 ${showTeams ? "op0" : "op1"}`}
      src={`/illustrations/${backgroundImage}.webp`}
    />,
    <div
      key={`content-${leverDeptUrl}`}
      className="df pos-r fdc h100p jcfe zIndex2 t-300"
      style={{bottom: showTeams || !isXs ? 0 : -10}}
    >
      <strong className="mb2 fs24 gray800 ls-3 font-cm fw5 lh13">{name}</strong>
      <span className="ttu mb2 font-mb fs12" style={{color}}>
        {i18n.t("{{count}} roles", {count: jobs.length})}
      </span>
      <div
        className="mv2 t-300 oh df fdc jcsb jcfs-xs"
        style={{
          height: showTeams ? "100%" : "0%",
          maxHeight: showTeams ? 500 : 0,
        }}
      >
        <ul className="font-ir fs14 mb4">
          {teams.map(team => (
            <li key={team} className="lh13 gray800">
              {team}
            </li>
          ))}
        </ul>
        <Btn
          fontSize={0.7}
          hoverBg={backgroundColor}
          hover={color}
          {...(isXs
            ? {
                href: leverDeptUrl,
                className: "focus-bsDarkBlue3-hug",
              }
            : {
                tabIndex: -1,
                type: "button",
              })}
        >
          {i18n.t("View These Roles")}
          <span className="cIcon-arrow-45deg fs8-f ml2" aria-hidden />
        </Btn>
      </div>
    </div>,
  ];

  // For valid DOM nesting
  const content = React.createElement(isMobile ? "div" : "a", wrapperProps, children);

  return (
    <div
      className="pos-r maxw25 minw100p-xs h72 minh30-xs hfc-xs mr3 mr0-xs mb5 mb0-xs"
      style={{zIndex}}
    >
      {content}
    </div>
  );
};

export default JobGroupCard;
